import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import useLearnProgress from "../../../hooks/useLearnProgress";
import { LETTER_GAME_CONFIGS } from "../../../data/letterGameConfigs";
import {
  LETTER_LESSONS, LETTERS, completedIdsForLetter, lessonRoute, nextLessonId, worldContainingLetter,
} from "../../../utils/learnLetterProgress";
import { ENGINE_MAP } from "./games";
import LearnStage from "../shell/LearnStage";
import Celebration from "../feedback/Celebration";
import useLearnAudio from "../audio/useLearnAudio";
import { LEARN, usePrefersReducedMotion } from "../theme";
import { letterBackgroundKey, prefetchLearnAssets } from "../media/learnAssets";

function worldRouteForLetter(letter) {
  const world = worldContainingLetter(letter);
  return `/learn/letters/${world?.id || "garden"}`;
}

const CHEERS = ["Great work!", "You did it!", "Nice one!", "Way to go!", "Super!"];

/**
 * A–Z letter course player. Reads :letter/:gameId, resumes the first
 * unfinished valid game, writes progress only on incomplete → complete.
 */
export default function LetterCoursePlayer() {
  const navigate = useNavigate();
  const { letter: letterParam, gameId } = useParams();
  const letter = String(letterParam || "").toUpperCase();
  const { courses, loading, markGameComplete, markLetterStarted } = useLearnProgress();
  const audio = useLearnAudio();
  const reduceMotion = usePrefersReducedMotion();

  const required = LETTER_LESSONS[letter] || [];
  const configs = LETTER_GAME_CONFIGS[letter] || {};
  const config = configs[gameId];
  const Engine = config ? ENGINE_MAP[config.engine] : null;
  const index = required.indexOf(gameId);
  const isValidGame = index >= 0 && !!config;
  const nextId = index >= 0 && index < required.length - 1 ? required[index + 1] : null;
  const isFinal = isValidGame && !nextId;
  const world = worldContainingLetter(letter);
  const theme = LEARN.world[world?.id] || { accent: LEARN.color.blue, deep: LEARN.color.blueDeep };

  const openedCompleteRef = useRef(completedIdsForLetter(courses, letter).has(gameId));
  const wroteRef = useRef(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [pose, setPose] = useState("idle");
  const [cheer] = useState(() => CHEERS[Math.floor(Math.random() * CHEERS.length)]);
  const prompt = config?.prompt || config?.instruction || "";

  // Session reset + voice: prefetch this prompt (and the next game's) and say it.
  useEffect(() => {
    openedCompleteRef.current = completedIdsForLetter(courses, letter).has(gameId);
    wroteRef.current = false;
    setSessionComplete(false);
    setPose("idle");
    audio.cancel();
    if (!config) return undefined;
    const nextCfg = nextId ? configs[nextId] : null;
    audio.prefetch([prompt, nextCfg?.prompt || nextCfg?.instruction].filter(Boolean));
    if (nextCfg?.background) prefetchLearnAssets([letterBackgroundKey(letter, nextCfg.background)]);
    audio.sfx("whoosh", { volume: 0.5 });
    const t = setTimeout(() => { if (prompt) audio.speak(prompt, { interrupt: true }); }, 350);
    return () => { clearTimeout(t); audio.cancel(); };
    // courses intentionally omitted — snapshot at entry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letter, gameId]);

  // Started marker (iOS parity: D unlocks a hidden storybook; other letters are informational).
  useEffect(() => {
    if (LETTERS.includes(letter) && !loading) markLetterStarted(letter);
  }, [letter, loading, markLetterStarted]);

  const goWorld = useCallback(() => {
    audio.cancel();
    navigate(worldRouteForLetter(letter));
  }, [audio, letter, navigate]);

  const handleComplete = useCallback(async () => {
    if (wroteRef.current) return;
    wroteRef.current = true;
    setPose("cheer");
    audio.cancel();
    audio.sfx(isFinal ? "levelup" : "sparkle");
    if (!openedCompleteRef.current) await markGameComplete(letter, gameId);
    setSessionComplete(true);
  }, [audio, gameId, isFinal, letter, markGameComplete]);

  const advance = useCallback(() => {
    setSessionComplete(false);
    if (nextId) {
      navigate(lessonRoute(letter, nextId), { replace: true });
      return;
    }
    goWorld();
  }, [goWorld, letter, navigate, nextId]);

  if (!LETTERS.includes(letter)) return <Navigate to="/learn/letters" replace />;

  if (!loading && !isValidGame) {
    const resumeId = nextLessonId(courses, letter) || required[0];
    return <Navigate to={resumeId ? lessonRoute(letter, resumeId) : worldRouteForLetter(letter)} replace />;
  }

  if (!isValidGame) return <LearnStage title={`Letter ${letter}`} accent={theme.accent} accentDeep={theme.deep} onClose={goWorld} />;

  const showCelebration = sessionComplete && !openedCompleteRef.current;

  return (
    <>
      <LearnStage
        title={config.title || `Letter ${letter}`}
        subtitle={`Letter ${letter} · Game ${index + 1} of ${required.length}`}
        prompt={prompt}
        background={letterBackgroundKey(letter, config.background) || undefined}
        accent={theme.accent}
        accentDeep={theme.deep}
        progress={{ done: index + (sessionComplete ? 1 : 0), total: required.length }}
        companionPose={pose}
        hint={config.hint}
        onClose={goWorld}
        onReplayPrompt={prompt ? () => audio.replay(prompt) : undefined}
        actionLabel={isFinal ? "Done" : "Next"}
        actionEnabled={sessionComplete}
        onAction={advance}
        speaking={audio.speaking}
      >
        {Engine ? (
          <Engine
            key={`${letter}-${gameId}`}
            letter={letter}
            gameId={gameId}
            config={config}
            speak={audio.speak}
            replay={audio.replay}
            sfx={audio.sfx}
            onComplete={handleComplete}
            onWrong={() => { setPose("oops"); audio.sfx("wrong", { volume: 0.6 }); }}
            reduceMotion={reduceMotion}
            alreadyCompleted={openedCompleteRef.current}
          />
        ) : (
          <p style={{ textAlign: "center", color: "#fff", fontWeight: 700, textShadow: "0 2px 8px rgba(11,26,51,0.5)" }}>This activity is not available yet.</p>
        )}
      </LearnStage>

      <Celebration
        open={showCelebration}
        kind={isFinal ? "trophy" : "star"}
        title={isFinal ? `Letter ${letter} complete!` : cheer}
        detail={isFinal ? `${world?.title || "The world"} just got a new medal.` : "Ready for the next game?"}
        cta={isFinal ? "Back to the map" : "Next game"}
        onCta={advance}
        reduceMotion={reduceMotion}
      />
    </>
  );
}
